import { z } from "zod";

export const SendEventDtoSchema = z.object({
  // Plain string, NOT the METRICS_EVENTS enum: the API compiles its own copy of the
  // enum, so a name a frontend ships before the next API deploy fails validation and
  // the event is lost for good. Callers keep compile-time narrowing through
  // `trackEvent(event: MetricsEvents, …)`; pinning the set again here only ever
  // rejected our own newer clients. Mixpanel accepts unknown names, and `safeTrack`
  // already takes a string.
  event: z.string(),
  // Plain string, NOT min(1): legacy flow/hub clients send "" when the Mixpanel SDK is
  // blocked, and a 400 here would poison whole bulk batches. The controller drops
  // empty-id events per element; arena's client never produces one.
  userId: z.string(),
  properties: z.record(z.string(), z.unknown()),
  // true once the visitor has a real identity (hub user), false while anonymous
  // (userId is the `$device:` id). Gates whether the backend attaches `$user_id`.
  // Optional so pre-flag cached clients still validate.
  isIdentified: z.boolean().optional(),
  // Stable per-event id generated at creation; dedups FE retries (Mixpanel $insert_id).
  insertId: z.string().min(1).max(64).optional(),
  // Event creation time; queued events keep their original timestamp. Strict ISO so a
  // malformed value is rejected at the boundary instead of silently losing the timestamp.
  occurredAt: z.iso.datetime().optional(),
});

export type SendEventDtoType = z.infer<typeof SendEventDtoSchema>;

/**
 * Elements are `unknown` on the wire on purpose: the bulk route parses each one with
 * {@link SendEventDtoSchema} and skips only what fails, so a single malformed event
 * cannot reject the batch it happened to travel with. Validating the array as one body
 * is what cost 463 users their events when a frontend shipped a name ahead of the API.
 */
export const SendEventsDtoSchema = z.object({
  events: z.array(z.unknown()),
});

/** Senders stay typed; the receiver is the tolerant side. */
export type SendEventsDtoType = { events: SendEventDtoType[] };
